import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Button,
  Collapse,
  List,
  Popconfirm,
  Rate,
  Space,
  Tag,
  Typography,
} from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import {
  initials,
  RELATIONSHIP_COLORS,
  SENTIMENT,
} from '@totem/components/accountPlan/utilities';

const StakeholdersCard = () => {
  const { data, openEditor, savePlan } = useContext(AccountPlanContext);
  if (!data) {
    return null;
  }
  const { plan, summary } = data;
  const mapped = new Set(
    plan.stakeholders.map((stakeholder) => stakeholder.email.toLowerCase()),
  );
  const unmapped = summary.contacts.filter(
    (contact) => !mapped.has(contact.email.toLowerCase()),
  );
  const names = new Map(
    plan.stakeholders.map((stakeholder) => [stakeholder.id, stakeholder.name]),
  );

  // Removing a manager moves their direct reports to the top level.
  const remove = (id: string) =>
    savePlan({
      ...plan,
      stakeholders: plan.stakeholders
        .filter((stakeholder) => stakeholder.id !== id)
        .map((stakeholder) =>
          stakeholder.reportsTo === id
            ? { ...stakeholder, reportsTo: '' }
            : stakeholder,
        ),
    });

  return (
    <PlanCard
      variant="manual"
      onEdit={() => openEditor({ section: 'stakeholder' })}
      title="Stakeholder Map"
      extra={
        plan.stakeholders.length > 0 && (
          <Link
            to="/dashboard/account/orgChart"
            style={{ color: '#ffffff', fontWeight: 600 }}
          >
            Org chart ↗
          </Link>
        )
      }
    >
      <List
        dataSource={plan.stakeholders}
        locale={{
          emptyText:
            'No stakeholders mapped yet — add them from the Portal contacts below.',
        }}
        renderItem={(stakeholder) => (
          <List.Item
            actions={[
              <Button
                key="edit"
                type="link"
                size="small"
                onClick={() =>
                  openEditor({ section: 'stakeholder', itemId: stakeholder.id })
                }
              >
                Edit
              </Button>,
              <Popconfirm
                key="remove"
                title="Remove this stakeholder?"
                onConfirm={() => remove(stakeholder.id)}
              >
                <Button type="link" size="small" danger>
                  Remove
                </Button>
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              avatar={<Avatar>{initials(stakeholder.name)}</Avatar>}
              title={
                <Space wrap size={4}>
                  {stakeholder.name}
                  <Tag
                    color={
                      RELATIONSHIP_COLORS[stakeholder.relationship] ?? 'default'
                    }
                  >
                    {stakeholder.relationship || 'Unknown'}
                  </Tag>
                  {SENTIMENT[stakeholder.sentiment] ?? ''}
                </Space>
              }
              description={
                <>
                  {[
                    stakeholder.role,
                    stakeholder.decisionRole,
                    stakeholder.orgType,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  {names.has(stakeholder.reportsTo ?? '') && (
                    <div>Reports to {names.get(stakeholder.reportsTo)}</div>
                  )}
                  <div>
                    <Rate
                      disabled
                      count={5}
                      value={stakeholder.influence}
                      style={{ fontSize: 12 }}
                    />
                  </div>
                </>
              }
            />
          </List.Item>
        )}
      />
      {unmapped.length > 0 && (
        <Collapse
          size="small"
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'contacts',
              label: `${unmapped.length} Portal contacts not yet on the map`,
              children: (
                <List
                  size="small"
                  dataSource={unmapped}
                  renderItem={(contact) => (
                    <List.Item
                      actions={[
                        <Button
                          key="add"
                          type="link"
                          size="small"
                          onClick={() =>
                            openEditor({
                              section: 'stakeholder',
                              seed: {
                                name: contact.name,
                                email: contact.email,
                                role: contact.labels.join(', '),
                                orgType: contact.orgType,
                              },
                            })
                          }
                        >
                          Add
                        </Button>,
                      ]}
                    >
                      <Typography.Text>{contact.name}</Typography.Text>{' '}
                      <Typography.Text type="secondary">
                        {[
                          contact.labels.join(', '),
                          contact.buildings.join(', '),
                          contact.orgType === 'Vendor' ? 'Vendor' : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography.Text>
                    </List.Item>
                  )}
                />
              ),
            },
          ]}
        />
      )}
    </PlanCard>
  );
};

export default StakeholdersCard;
